/**
 * DecreeLinesEditor.tsx — Dekretacja faktury: EDYTOWALNA siatka księgowań Wn/Ma.
 *
 * Architektura mirror `DocumentItemsEditor` (patrz nagłówek tamtego pliku) — w
 * PEŁNI kontrolowany z zewnątrz (`value`/`onChange`), ZERO własnego fetch/mutate
 * STANU DEKRETU. Zapis (payload POST/PATCH faktury) i wpięcie na stronę faktury —
 * patrz nagłówek `lib/documents/decree-lines.ts`.
 *
 * Wyjątek od „zero fetch": DANE REFERENCYJNE planu kont (lata obrachunkowe + wartości
 * segmentów do podpowiedzi) czyta ten komponent sam, gdy dostanie `companyId` — to odczyt
 * cache'owany react-query, nigdy stan dekretu. Bez `companyId` formatka działa jak
 * wcześniej: same pola tekstowe, bez strzałek i bez selektora roku.
 *
 * Konto — 6 ZAWSZE WIDOCZNYCH kolumn segmentów tekstowych (Syntetyka +
 * Pozycja 1-5, wzorem WF `DocumentAccountingEntry`/`DocumentAccountingTable`).
 * Każde pole zostaje polem TEKSTOWYM (można wpisać konto spoza planu — backend segmentów
 * nie waliduje), a obok wartości ma strzałkę rozwijającą listę z planu kont wybranego roku
 * (kaskadowo: opcje zależą od segmentów na lewo) — `AccountSegmentCombobox`.
 *
 * Model danych: wiersze grupowane po `groupNo` („pozycje" — para/zestaw Wn+Ma).
 * Jedna kwota + `side` per wiersz w modelu, ale UI prezentuje wiersze jak DWIE
 * wirtualne kolumny „Wn”/„Ma” — kwota renderuje się w komórce odpowiadającej
 * `side`, druga komórka pokazuje przycisk „przenieś tutaj” (zmienia `side`,
 * zachowując kwotę). To JEST przełącznik Wn/Ma z opisu zadania — cała para
 * kolumn działa jak toggle, nie osobny kontrolek.
 *
 * Bilans: wskaźnik PER GRUPA (kolor/badge gdy ΣWn≠ΣMa tej grupy) + pasek
 * bilansu OGÓLNEGO na dole (suma wszystkich grup). Wyłącznie prezentacyjny —
 * zapis dekretu zawsze się udaje niezależnie od bilansu. „Domknij różnicę" per
 * grupa woła `autoBalanceGroup` (dopisuje wiersz na stronie deficytowej z kwotą
 * różnicy, konto puste — user musi je uzupełnić, patrz nagłówek lib pliku).
 */

import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FK_MAX_DATE_YEAR, FK_MIN_DATE_YEAR } from '@saas/shared';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { TableEmpty } from '@/components/configurable-table/TableEmpty';
import { DatePicker } from '@/components/DatePicker';
import { AccountSegmentCombobox } from '@/components/documents/AccountSegmentCombobox';
import { Dropdown } from '@/components/Dropdown';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Spinner } from '@/components/Spinner';
import {
  EMPTY_SEGMENT_INDEX,
  useChartFiscalYears,
  useChartSegmentIndex,
  useSymfoniaContractorPositions,
} from '@/lib/documents/chart-of-accounts-segments';
import {
  ACCOUNT_SEGMENT_FIELDS,
  accountSegmentLabel,
  addLineToGroup,
  autoBalanceGroup,
  BEHIND_POSTING_VALUES,
  behindPostingDescription,
  behindPostingLabel,
  behindPostingOptionLabel,
  type BehindPostingValue,
  computeGroupBalance,
  computeOverallBalance,
  type DecreeLineDraft,
  type DecreeLineSide,
  groupDecreeLines,
  isInvalidAmount,
  normalizeBehindPosting,
  removeDecreeGroup,
  removeDecreeLine,
  seedGroup,
  updateDecreeLine,
} from '@/lib/documents/decree-lines';

/** Kontekst pola „Typ dokumentu Symfonia" — z GET faktury (`invoice.symfoniaType`). */
export interface SymfoniaTypeContext {
  /** Lista wyboru. PUSTA = firma bez aktywnej integracji FK → pole się nie pokazuje. */
  options: { code: string; label: string }[];
  /** Kod z mapowania typów w integracji; `null` = typ dokumentu niezmapowany. */
  defaultCode: string | null;
}

export interface DecreeLinesEditorProps {
  value: DecreeLineDraft[];
  onChange: (next: DecreeLineDraft[]) => void;
  /**
   * Firma dokumentu — włącza podpowiedzi z planu kont (jawnie z propsa, NIE z globalnego
   * `X-Company-Id`: firma dokumentu może różnić się od wybranej w top-barze — ten sam powód
   * co w `AccountSearchPicker`). Brak → formatka bez podpowiedzi.
   */
  companyId?: string | null;
  /**
   * Typ dokumentu w Symfonii: lista wyboru + wartość domyślna z mapowania w integracji.
   * Dane REFERENCYJNE (nie stan dokumentu) — dlatego przychodzą gotowe z GET, a nie są
   * tu dociągane; brak → pole ukryte.
   */
  symfoniaType?: SymfoniaTypeContext | null;
  /**
   * NADPISANIE typu dla tego dokumentu; `null`/pusty = „użyj domyślnego z integracji".
   * Stan dokumentu — w pełni kontrolowany z zewnątrz (mirror `value`/`onChange` wierszy).
   */
  symfoniaTypeCode?: string | null;
  onSymfoniaTypeCodeChange?: (code: string | null) => void;
  /**
   * Rok obrachunkowy DOKUMENTU (zapisany, nie tylko wybrany w formatce) — decyduje, z którego
   * planu kont podpowiadamy konta i do którego roku dokument pójdzie do Symfonii. `null` =
   * dokument nie ma jeszcze przypisanego roku; wybieramy wtedy najnowszy zaimportowany i
   * raportujemy go przez `onFiscalYearChange`, żeby zapisał się przy najbliższym zapisie.
   */
  fiscalYear?: string | null;
  onFiscalYearChange?: (fiscalYear: string) => void;
  /**
   * „Data okresu" = miesiąc księgowy w Symfonii FK (`YYYY-MM-01` albo `null`). To BIEŻĄCY
   * stan roboczy (draft) — kontrolowany z zewnątrz, jak `symfoniaTypeCode`; podpowiedź z daty
   * dokumentu i „lepkość" ręcznego wyboru liczy strona dokumentu
   * (`useSymfoniaPeriodDateDraft`), bo edytor odmontowuje się przy zmianie zakładki. Pole widać
   * tylko wtedy, gdy widać też typ dokumentu Symfonia (ta sama bramka: firma z integracją FK)
   * i podano `onPeriodDateChange`.
   */
  periodDate?: string | null;
  /** RĘCZNA zmiana Daty okresu (wybór miesiąca albo `null` = wyczyszczenie). */
  onPeriodDateChange?: (value: string | null) => void;
  /**
   * Wołane RAZ po zamontowaniu, gdy pole Daty okresu jest widoczne — sygnał dla strony, że
   * użytkownik zajrzał do Dekretacji i można włączyć podpowiedź z daty dokumentu.
   */
  onPeriodDateActivate?: () => void;
  /**
   * Komunikat odrzucenia Daty okresu przez serwer (np. rocznik spoza zakresu FK) — czerwona
   * ramka pola + tekst pod nim. Stan błędów trzyma strona (`useFieldErrors`); `null`/brak = ok.
   */
  periodDateError?: string | null;
  /** Wiersz akcji pod nagłówkiem (np. podpowiedź dekretu, procedury) — nad tabelą. */
  toolbar?: React.ReactNode;
  /** Treść po prawej stronie wiersza nagłówka (np. plakietka „Wysłano…"). */
  headerAside?: React.ReactNode;
  'data-testid'?: string;
}

const th = 'px-2 py-2 text-left text-xs font-semibold text-text-secondary whitespace-nowrap';
const thRight = 'px-2 py-2 text-right text-xs font-semibold text-text-secondary whitespace-nowrap';
const td = 'px-2 py-1 align-top';

/**
 * Wspólny układ kolumn dla WSZYSTKICH tabel grup (i pustego stanu) — `table-fixed` +
 * jeden `<colgroup>`, żeby kolumny w kolejnych grupach były wyrównane (każda grupa to
 * osobna tabela). Syntetyka szersza niż segmenty analityczne; opis dostaje resztę.
 *
 * Minimalna szerokość tabeli = suma stałych kolumn (67 rem) + co najmniej 12 rem na opis
 * (komórka 12 rem minus marginesy komórki i pola ≈ 20 znaków widocznego tekstu). Przy węższym
 * oknie tabela przewija się w poziomie, ale opis nie spada poniżej 12 rem (wcześniej ok. 5 rem).
 *
 * ZMIENIASZ SZEROKOŚĆ KOLUMNY → przelicz `min-w` w `TABLE_CLASS` (Tailwind wymaga statycznej
 * klasy, więc sumy nie da się policzyć w kodzie). Pilnuje tego test „suma kolumn + 12 rem".
 */
const COL_SEGMENT_FIRST = 'w-28';
const COL_SEGMENT = 'w-24';
const COL_AMOUNT = 'w-32';
const COL_TRANSACTION = 'w-20';
const COL_BEHIND_POSTING = 'w-24';
const COL_ACTIONS = 'w-12';
const TABLE_CLASS = 'w-full min-w-[79rem] table-fixed text-sm';

function DecreeColGroup() {
  return (
    <colgroup>
      {ACCOUNT_SEGMENT_FIELDS.map((field, depth) => (
        <col key={field} className={depth === 0 ? COL_SEGMENT_FIRST : COL_SEGMENT} />
      ))}
      {/* Opis — bez szerokości: zajmuje całą resztę. */}
      <col />
      <col className={COL_AMOUNT} />
      <col className={COL_AMOUNT} />
      <col className={COL_TRANSACTION} />
      <col className={COL_BEHIND_POSTING} />
      <col className={COL_ACTIONS} />
    </colgroup>
  );
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Komórka wirtualnej kolumny Wn/Ma — input edytowalny gdy `active` (wiersz jest
 * na TEJ stronie), inaczej przycisk „przenieś tutaj” (zmienia `side`, kwota zostaje). */
function AmountCell({
  active,
  amount,
  amountAriaLabel,
  moveHereLabel,
  onAmountChange,
  onMoveHere,
  'data-testid': testId,
}: {
  active: boolean;
  amount: string;
  amountAriaLabel: string;
  moveHereLabel: string;
  onAmountChange: (value: string) => void;
  onMoveHere: () => void;
  'data-testid'?: string;
}) {
  if (!active) {
    // Świadomy wyjątek od migracji na `Button`/`IconButton` (finding [D], pre-commit review
    // 2026-09-02) — ten przycisk wypełnia CAŁĄ komórkę tabeli (`w-full`, minimalny pionowy
    // padding, bez poziomego), a żaden z dwóch prymitywów nie ma wariantu „gęsta komórka
    // tabeli"; wymuszenie przez nadpisania className ryzykowałoby regresję wizualną bez
    // możliwości weryfikacji w przeglądarce w tej sesji. Dług odnotowany jawnie, nie po cichu.
    return (
      <td className={`${td} text-center`}>
        <button
          type="button"
          onClick={onMoveHere}
          aria-label={moveHereLabel}
          className="w-full rounded border border-dashed border-border-secondary py-1 text-xs text-text-muted hover:border-accent-400 hover:text-accent-600"
          data-testid={testId ? `${testId}-move` : undefined}
        >
          —
        </button>
      </td>
    );
  }
  // Wartość niepusta i nienumeryczna (literówka/wklejony tekst) — podświetlenie
  // czerwoną ramką zamiast cichego liczenia jako 0 w bilansie (finding [F]; `computeBalance`
  // w `lib/documents/decree-lines.ts` już wymusza `balanced: false` dla takiego wiersza —
  // ten warunek to WYŁĄCZNIE wizualny sygnał w tej samej logice). Delegowane do propa
  // `invalid` komponentu `Input` (patrz `Input.tsx`) zamiast ręcznego dublowania warunku
  // `border-red-500` — unika dwóch klas `border-*`/`focus:ring-*` tej samej specyficzności
  // na jednym elemencie (zwycięzca zależałby od kolejności w wygenerowanym CSS Tailwinda,
  // nie od kolejności w `className`).
  const invalid = isInvalidAmount(amount);
  return (
    <td className={td}>
      <Input
        className="text-right tabular-nums"
        inputMode="decimal"
        value={amount}
        onChange={(e) => onAmountChange(e.target.value.replace(/,/g, '.'))}
        aria-label={amountAriaLabel}
        invalid={invalid}
        data-testid={testId}
      />
    </td>
  );
}

export function DecreeLinesEditor({
  value,
  onChange,
  companyId,
  symfoniaType,
  symfoniaTypeCode,
  onSymfoniaTypeCodeChange,
  fiscalYear: fiscalYearProp,
  onFiscalYearChange,
  periodDate,
  onPeriodDateChange,
  onPeriodDateActivate,
  periodDateError,
  toolbar,
  headerAside,
  'data-testid': testId = 'decree-lines-editor',
}: DecreeLinesEditorProps) {
  const { t } = useTranslation();

  const groups = useMemo(() => groupDecreeLines(value), [value]);
  const overallBalance = useMemo(() => computeOverallBalance(value), [value]);

  // Plan kont jest per (firma, ROK) — bez wybranego roku nie ma z czego podpowiadać.
  // Rok przypisany do dokumentu wygrywa; gdy dokument go nie ma, bierzemy najnowszy
  // zaimportowany (endpoint sortuje po ostatnim imporcie) i RAPORTUJEMY na zewnątrz, żeby
  // zapisał się razem z dekretem — inaczej w trybie przeglądania nie byłoby widać, do
  // którego roku dokument należy.
  const yearsQ = useChartFiscalYears(companyId);
  // Pozycje kontrahentów dla poziomu analityki oznaczonego w planie kont jako kartoteka
  // kontrahentów (konta rozrachunkowe). Bez tego pole pokazywało samą liczbę bez nazwy.
  const contractorPositionsQ = useSymfoniaContractorPositions(companyId);
  const years = useMemo(() => yearsQ.data ?? [], [yearsQ.data]);
  const [fiscalYear, setFiscalYear] = useState<string | null>(fiscalYearProp ?? null);
  useEffect(() => {
    const effective = fiscalYearProp ?? (years.length > 0 ? years[0]!.fiscalYear : null);
    if (effective === null || effective === fiscalYear) return;
    setFiscalYear(effective);
    onFiscalYearChange?.(effective);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalYearProp, yearsQ.data]);

  function pickFiscalYear(next: string | null) {
    setFiscalYear(next);
    if (next) onFiscalYearChange?.(next);
  }

  const indexQ = useChartSegmentIndex(companyId, fiscalYear);
  const segmentIndex = indexQ.data ?? EMPTY_SEGMENT_INDEX;
  const selectedYear = years.find((y) => y.fiscalYear === fiscalYear);
  // Brak planu kont (lista lat wczytana, pusta) — zamiast po cichu chować pole roku pokazujemy,
  // skąd go wziąć (szkolenie 14.10: „nie ma pola rok obrachunkowy” = brak importu planu kont).
  const noChart = yearsQ.isSuccess && years.length === 0;

  // Pole typu dokumentu pokazujemy tylko, gdy firma ma z czego wybierać (aktywna integracja
  // FK) i gdy jest komu oddać zmianę — bez handlera byłby to martwy kontrolek.
  const showSymfoniaType =
    !!symfoniaType && symfoniaType.options.length > 0 && !!onSymfoniaTypeCodeChange;
  const showPeriodDate = showSymfoniaType && !!onPeriodDateChange;
  // Id komunikatu błędu Daty okresu — unikalny per instancja edytora (`aria-describedby`).
  const periodDateErrorId = useId();

  // Data okresu — edytor tylko ZGŁASZA, że pole jest widoczne (strona włącza wtedy podpowiedź
  // z daty dokumentu). Sam niczego nie podpowiada ani nie pamięta: ten stan ginąłby razem
  // z edytorem przy przełączeniu zakładki (patrz `useSymfoniaPeriodDateDraft`).
  useEffect(() => {
    if (showPeriodDate) onPeriodDateActivate?.();
  }, [showPeriodDate, onPeriodDateActivate]);

  function patchLine(key: string, patch: Partial<DecreeLineDraft>) {
    onChange(updateDecreeLine(value, key, patch));
  }

  function moveLineToSide(key: string, side: DecreeLineSide) {
    patchLine(key, { side });
  }

  function addPosition() {
    onChange(seedGroup(value));
  }

  function addLine(groupNo: number, side: DecreeLineSide) {
    onChange(addLineToGroup(value, groupNo, side));
  }

  function removeLine(key: string) {
    onChange(removeDecreeLine(value, key));
  }

  function removeGroup(groupNo: number) {
    onChange(removeDecreeGroup(value, groupNo));
  }

  function closeGroupBalance(groupNo: number) {
    onChange(autoBalanceGroup(value, groupNo));
  }

  return (
    <div className="space-y-4" data-testid={testId}>
      {/* Nagłówek formatki — JEDEN wiersz: Rok obrachunkowy | Typ dokumentu Symfonia | Data
          okresu (trzy równe kolumny, etykieta nad kontrolką) + `headerAside` na prawym końcu.
          Rok widoczny tylko gdy firma ma zaimportowany choć jeden plan; bez niego strzałki w
          polach kont się nie pojawiają. */}
      {(years.length > 0 || noChart || showSymfoniaType || !!headerAside) && (
        <div className="flex flex-wrap items-start gap-x-4 gap-y-2" data-testid="decree-header-bar">
          <div className="grid min-w-0 flex-1 grid-cols-1 items-start gap-3 sm:grid-cols-3">
            {years.length > 0 && (
              <div className="min-w-0 space-y-1" data-testid="decree-chart-year-bar">
                <span className="block text-xs font-medium text-text-secondary">
                  {t('tenant.documents.decree.editor.fiscalYearLabel')}
                </span>
                <div className="flex items-center gap-2">
                  <Dropdown
                    value={fiscalYear ?? ''}
                    triggerText={
                      selectedYear
                        ? t('tenant.documents.decree.editor.chartPlanValue', {
                            year: selectedYear.fiscalYear,
                            count: selectedYear.accountCount,
                          })
                        : undefined
                    }
                    onChange={(val) => pickFiscalYear(val || null)}
                    options={years.map((y) => ({
                      value: y.fiscalYear,
                      label: t('tenant.documents.decree.editor.chartPlanValue', {
                        year: y.fiscalYear,
                        count: y.accountCount,
                      }),
                    }))}
                    aria-label={t('tenant.documents.decree.editor.fiscalYearLabel')}
                    className="w-full"
                    data-testid="decree-chart-year-select"
                  />
                  {indexQ.isLoading && <Spinner />}
                </div>
              </div>
            )}
            {noChart && (
              <div className="min-w-0 space-y-1" data-testid="decree-chart-year-missing">
                <span className="block text-xs font-medium text-text-secondary">
                  {t('tenant.documents.decree.editor.fiscalYearLabel')}
                </span>
                <p className="text-xs text-text-muted">
                  {t('tenant.documents.decree.editor.noChartOfAccounts')}
                </p>
              </div>
            )}

            {/* Typ dokumentu Symfonia — domyślnie wartość z mapowania w integracji (pierwsza
                opcja, wartość ''), do zmiany dla TEGO jednego dokumentu. Pusty wybór zapisuje
                null, więc dokument wraca pod mapowanie i podąża za jego zmianami. */}
            {showSymfoniaType && (
              <div className="min-w-0 space-y-1" data-testid="decree-symfonia-type-bar">
                <span className="block text-xs font-medium text-text-secondary">
                  {t('tenant.documents.decree.editor.symfoniaTypeLabel')}
                </span>
                <div>
                  <Dropdown
                    value={symfoniaTypeCode ?? ''}
                    onChange={(val) => onSymfoniaTypeCodeChange?.(val || null)}
                    options={[
                      {
                        value: '',
                        label: symfoniaType!.defaultCode
                          ? t('tenant.documents.decree.editor.symfoniaTypeInherit', {
                              code: symfoniaType!.defaultCode,
                            })
                          : t('tenant.documents.decree.editor.symfoniaTypeNoDefault'),
                      },
                      ...symfoniaType!.options.map((o) => ({
                        value: o.code,
                        label: t('tenant.documents.decree.editor.symfoniaTypeOption', {
                          code: o.code,
                          label: o.label,
                        }),
                      })),
                    ]}
                    aria-label={t('tenant.documents.decree.editor.symfoniaTypeLabel')}
                    className="w-full"
                    data-testid="decree-symfonia-type-select"
                  />
                  {/* Jawny sygnał odstępstwa od konfiguracji — inaczej po miesiącach nikt nie
                      wie, czemu ten jeden dokument ma inny typ niż reszta. */}
                  {!!symfoniaTypeCode && !!symfoniaType!.defaultCode && (
                    <p
                      className="mt-0.5 text-[10px] leading-tight text-text-tertiary"
                      data-testid="decree-symfonia-type-overridden"
                    >
                      {t('tenant.documents.decree.editor.symfoniaTypeOverridden', {
                        code: symfoniaType!.defaultCode,
                      })}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Data okresu = miesiąc księgowy w Symfonii; ta sama bramka co typ dokumentu. */}
            {showPeriodDate && (
              <div className="min-w-0 space-y-1" data-testid="decree-period-date-bar">
                <span className="block text-xs font-medium text-text-secondary">
                  {t('tenant.documents.decree.editor.periodDateLabel')}
                </span>
                <DatePicker
                  mode="month"
                  value={periodDate ?? ''}
                  onChange={(next) => onPeriodDateChange?.(next || null)}
                  minYear={FK_MIN_DATE_YEAR}
                  maxYear={FK_MAX_DATE_YEAR}
                  invalid={!!periodDateError}
                  aria-label={t('tenant.documents.decree.editor.periodDateLabel')}
                  aria-describedby={periodDateError ? periodDateErrorId : undefined}
                  data-testid="decree-period-date"
                />
                {!!periodDateError && (
                  <span
                    id={periodDateErrorId}
                    className="block text-xs text-red-600"
                    role="alert"
                    data-testid="decree-period-date-error"
                  >
                    {periodDateError}
                  </span>
                )}
              </div>
            )}
          </div>

          {!!headerAside && (
            <div className="ml-auto shrink-0 self-end" data-testid="decree-header-aside">
              {headerAside}
            </div>
          )}
        </div>
      )}

      {!!toolbar && (
        <div className="flex flex-wrap items-center gap-2" data-testid="decree-toolbar">
          {toolbar}
        </div>
      )}

      {groups.length === 0 ? (
        <div className="overflow-hidden rounded-lg border border-border-primary">
          <div className="overflow-x-auto">
            <table className={TABLE_CLASS}>
              <DecreeColGroup />
              <thead>
                <tr className="border-b border-border-primary">
                  {ACCOUNT_SEGMENT_FIELDS.map((field) => (
                    <th key={field} className={th} scope="col">
                      {accountSegmentLabel(field, t)}
                    </th>
                  ))}
                  <th className={th} scope="col">
                    {t('tenant.documents.decree.columns.description')}
                  </th>
                  <th className={thRight} scope="col">
                    {t('tenant.documents.decree.columns.debit')}
                  </th>
                  <th className={thRight} scope="col">
                    {t('tenant.documents.decree.columns.credit')}
                  </th>
                  <th className={th} scope="col">
                    {t('tenant.documents.decree.columns.transaction')}
                  </th>
                  <th className={th} scope="col">
                    {t('tenant.documents.decree.columns.behindPosting')}
                  </th>
                  <th className={th} scope="col">
                    <span className="sr-only">{t('tenant.documents.decree.editor.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={ACCOUNT_SEGMENT_FIELDS.length + 6} data-testid="decree-lines-empty">
                    <TableEmpty
                      title={t('tenant.documents.decree.editor.emptyTitle')}
                      description={t('tenant.documents.decree.editor.emptyHint')}
                      action={
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={addPosition}
                          data-testid="decree-lines-empty-add"
                        >
                          <Plus className="mr-1 h-3.5 w-3.5" />
                          {t('tenant.documents.decree.editor.addPosition')}
                        </Button>
                      }
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(({ groupNo, lines }) => {
            const groupBalance = computeGroupBalance(lines);
            return (
              <div
                key={groupNo}
                className="rounded-lg border border-border-primary"
                data-testid={`decree-group-${groupNo}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border-primary bg-bg-secondary px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-text-primary">
                      {t('tenant.documents.decree.editor.groupLabel', { number: groupNo })}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        groupBalance.balanced
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                      }`}
                      data-testid={`decree-group-${groupNo}-balance-badge`}
                    >
                      {groupBalance.balanced
                        ? t('tenant.documents.decree.balance.balanced')
                        : t('tenant.documents.decree.balance.diff', {
                            amount: formatMoney(Math.abs(groupBalance.diff)),
                          })}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {!groupBalance.balanced && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => closeGroupBalance(groupNo)}
                        data-testid={`decree-group-${groupNo}-close-balance`}
                      >
                        {t('tenant.documents.decree.editor.closeBalance')}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeGroup(groupNo)}
                      data-testid={`decree-group-${groupNo}-remove`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className={TABLE_CLASS}>
                    <DecreeColGroup />
                    <thead>
                      <tr className="border-b border-border-primary">
                        {ACCOUNT_SEGMENT_FIELDS.map((field) => (
                          <th key={field} className={th} scope="col">
                            {accountSegmentLabel(field, t)}
                          </th>
                        ))}
                        <th className={th} scope="col">
                          {t('tenant.documents.decree.columns.description')}
                        </th>
                        <th className={thRight} scope="col">
                          {t('tenant.documents.decree.columns.debit')}
                        </th>
                        <th className={thRight} scope="col">
                          {t('tenant.documents.decree.columns.credit')}
                        </th>
                        <th className={th} scope="col">
                          {t('tenant.documents.decree.columns.transaction')}
                        </th>
                        <th className={th} scope="col">
                          {t('tenant.documents.decree.columns.behindPosting')}
                        </th>
                        <th className={th} scope="col">
                          <span className="sr-only">
                            {t('tenant.documents.decree.editor.actions')}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <tr
                          key={line.key}
                          className="border-b border-border-primary last:border-0"
                          data-testid={`decree-line-row-${line.key}`}
                        >
                          {ACCOUNT_SEGMENT_FIELDS.map((field, depth) => (
                            <td key={field} className={td}>
                              <AccountSegmentCombobox
                                value={line[field] ?? ''}
                                onChange={(next) => patchLine(line.key, { [field]: next })}
                                ariaLabel={accountSegmentLabel(field, t)}
                                index={segmentIndex}
                                prefixSegments={ACCOUNT_SEGMENT_FIELDS.slice(0, depth).map(
                                  (f) => line[f] ?? '',
                                )}
                                contractorPositions={contractorPositionsQ.data}
                                data-testid={`decree-line-${field}-${line.key}`}
                              />
                            </td>
                          ))}
                          <td className={td}>
                            <Input
                              value={line.description ?? ''}
                              onChange={(e) => patchLine(line.key, { description: e.target.value })}
                              maxLength={1000}
                              // Pełny opis po najechaniu — w wąskiej kolumnie długi tekst się ucina.
                              title={line.description || undefined}
                              aria-label={t('tenant.documents.decree.columns.description')}
                              data-testid={`decree-line-description-${line.key}`}
                            />
                          </td>
                          <AmountCell
                            active={line.side === 'debit'}
                            amount={line.amount}
                            amountAriaLabel={t('tenant.documents.decree.columns.debit')}
                            moveHereLabel={t('tenant.documents.decree.editor.moveToDebit')}
                            onAmountChange={(v) => patchLine(line.key, { amount: v })}
                            onMoveHere={() => moveLineToSide(line.key, 'debit')}
                            data-testid={`decree-line-debit-${line.key}`}
                          />
                          <AmountCell
                            active={line.side === 'credit'}
                            amount={line.amount}
                            amountAriaLabel={t('tenant.documents.decree.columns.credit')}
                            moveHereLabel={t('tenant.documents.decree.editor.moveToCredit')}
                            onAmountChange={(v) => patchLine(line.key, { amount: v })}
                            onMoveHere={() => moveLineToSide(line.key, 'credit')}
                            data-testid={`decree-line-credit-${line.key}`}
                          />
                          <td className={`${td} text-center`}>
                            <Checkbox
                              checked={line.isTransaction ?? false}
                              onChange={(e) =>
                                patchLine(line.key, { isTransaction: e.target.checked })
                              }
                              aria-label={t('tenant.documents.decree.columns.transaction')}
                              data-testid={`decree-line-transaction-${line.key}`}
                            />
                          </td>
                          <td className={td}>
                            {(() => {
                              // W komórce (zwinięta kontrolka) SAMA liczba — to ona trafia do
                              // kolumny `behind_posting` i dalej do Symfonii. Opis pokazujemy
                              // na rozwiniętej liście oraz w `title` po najechaniu.
                              const bp = normalizeBehindPosting(line.behindPosting);
                              return (
                                <Dropdown
                                  value={String(bp)}
                                  triggerText={behindPostingLabel(bp)}
                                  onChange={(val) =>
                                    patchLine(line.key, {
                                      behindPosting: Number(val) as BehindPostingValue,
                                    })
                                  }
                                  options={BEHIND_POSTING_VALUES.map((v) => ({
                                    value: String(v),
                                    label: behindPostingOptionLabel(v, t),
                                  }))}
                                  aria-label={`${t('tenant.documents.decree.columns.behindPosting')}: ${behindPostingDescription(bp, t)}`}
                                  className="tabular-nums"
                                  data-testid={`decree-line-behind-posting-${line.key}`}
                                />
                              );
                            })()}
                          </td>
                          <td className="px-2 py-1 text-center align-top">
                            <IconButton
                              size="sm"
                              onClick={() => removeLine(line.key)}
                              className="hover:text-red-600"
                              aria-label={t('tenant.documents.decree.editor.removeLine')}
                              data-testid={`decree-line-remove-${line.key}`}
                            >
                              <Trash2 />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center gap-2 border-t border-border-primary px-3 py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => addLine(groupNo, 'debit')}
                    data-testid={`decree-group-${groupNo}-add-debit`}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    {t('tenant.documents.decree.editor.addDebitLine')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => addLine(groupNo, 'credit')}
                    data-testid={`decree-group-${groupNo}-add-credit`}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    {t('tenant.documents.decree.editor.addCreditLine')}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Gdy pusto, jedyny przycisk „Dodaj pozycję" to ten wewnątrz TableEmpty
            (testid decree-lines-empty-add) — ten footerowy dubluje etykietę (finding [K]). */}
        {groups.length > 0 && (
          <Button variant="ghost" onClick={addPosition} data-testid="decree-add-position">
            <Plus className="mr-1 h-4 w-4" />
            {t('tenant.documents.decree.editor.addPosition')}
          </Button>
        )}

        <div
          className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm"
          data-testid="decree-overall-balance"
        >
          <span className="text-text-secondary">
            {t('tenant.documents.decree.balance.totalDebit')}:{' '}
            <span className="font-semibold tabular-nums text-text-primary">
              {formatMoney(overallBalance.debit)}
            </span>
          </span>
          <span className="text-text-secondary">
            {t('tenant.documents.decree.balance.totalCredit')}:{' '}
            <span className="font-semibold tabular-nums text-text-primary">
              {formatMoney(overallBalance.credit)}
            </span>
          </span>
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
              overallBalance.balanced
                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
            }`}
            data-testid="decree-overall-balance-badge"
          >
            {overallBalance.balanced
              ? t('tenant.documents.decree.balance.balanced')
              : t('tenant.documents.decree.balance.notBalanced')}
          </span>
        </div>
      </div>
    </div>
  );
}
